import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import api, { employeeMasterAPI, bulkUploadAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, XCircle, Download, RefreshCw, Copy, Merge, CheckSquare, FileUp, Users, Trash2, RotateCcw
} from 'lucide-react';
import { BulkUploadModal } from '../components/employees/BulkUploadModal';
import { 
  DeleteEmployeeDialog, 
  BulkDeleteDialog, 
  BulkRestoreDialog
} from '../components/employees/dialogs';
import { HelpTooltip } from '../components/HelpTooltip';

// Import modular components
import { EmployeeStatsCards, TABS } from '../components/employee-master/EmployeeStatsCards';
import { EmployeePagination } from '../components/employee-master/EmployeePagination';
import { EmployeeTable } from '../components/employee-master/EmployeeTable';
import { EmployeeFilters } from '../components/employee-master/EmployeeFilters';

// Sortable columns configuration
const SORTABLE_COLUMNS = [
  { id: 'login_id', label: 'Login ID' },
  { id: 'department', label: 'Department' },
  { id: 'designation', label: 'Designation' },
  { id: 'grade', label: 'Grade' },
  { id: 'status', label: 'Status' },
  { id: 'auth_status', label: 'Auth Status' },
];

const EmployeeMasterList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  
  // Check if user can see login_id (admin or verifier only)
  const canSeeLoginId = isAdmin() || user?.role === 'verifier';
  
  // Active tab from URL or default to 'total'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'total');
  
  // Sorting state
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || '');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'asc');
  
  // Stats for tab counts
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending_auth: 0,
    on_probation: 0,
    incomplete_profiles: 0,
    deleted: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    employee: null,
    hardDelete: false,
    loading: false
  });
  
  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Bulk action dialogs
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({
    open: false,
    hardDelete: false,
    loading: false
  });
  
  const [bulkRestoreDialog, setBulkRestoreDialog] = useState({
    open: false,
    loading: false
  });
  
  // Duplicates dialog
  const [duplicatesDialog, setDuplicatesDialog] = useState({
    open: false,
    loading: false,
    field: 'email',
    duplicates: []
  });
  
  // Merge dialog
  const [mergeDialog, setMergeDialog] = useState({
    open: false,
    loading: false,
    primaryId: null,
    secondaryId: null,
    primary: null,
    secondary: null
  });
  
  // Bulk Upload Modal state
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    department_id: '',
    status: '',
    authorization_status: '',
    employment_type: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/employee-master/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters, activeTab, sortBy, sortOrder]);

  // Update URL when tab or sort changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (activeTab === 'total') {
      params.delete('tab');
    } else {
      params.set('tab', activeTab);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
      params.set('sort_order', sortOrder);
    } else {
      params.delete('sort_by');
      params.delete('sort_order');
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sortBy, sortOrder]);

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/departments');
      setDepartments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch departments');
      setDepartments([]);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        tab: activeTab !== 'total' ? activeTab : undefined
      };
      
      if (filters.search) params.search = filters.search;
      if (filters.department_id) params.department_id = filters.department_id;
      if (filters.status) params.status = filters.status;
      if (filters.authorization_status) params.authorization_status = filters.authorization_status;
      if (filters.employment_type) params.employment_type = filters.employment_type;
      
      // Add sorting params
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
      }
      
      const res = await api.get('/employee-master', { params });
      setEmployees(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1); // Reset to first page
    // Clear conflicting filters when switching tabs
    setFilters(prev => ({
      ...prev,
      status: tabId === 'deleted' ? 'deleted' : '',
      authorization_status: ''
    }));
    // Maintain sorting when switching tabs
  };

  // Delete employee handler
  const handleDeleteEmployee = async () => {
    if (!deleteDialog.employee) return;
    
    setDeleteDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await employeeMasterAPI.deleteEmployee(deleteDialog.employee.id, deleteDialog.hardDelete);
      toast.success(
        deleteDialog.hardDelete 
          ? `Employee ${deleteDialog.employee.employee_code} permanently deleted` 
          : `Employee ${deleteDialog.employee.employee_code} marked as deleted`
      );
      setDeleteDialog({ open: false, employee: null, hardDelete: false, loading: false });
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete employee');
      setDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Restore employee handler
  const handleRestoreEmployee = async (employee) => {
    try {
      await employeeMasterAPI.restoreEmployee(employee.id);
      toast.success(`Employee ${employee.employee_code} restored successfully`);
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore employee');
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === employees.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(employees.map(e => e.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    setBulkDeleteDialog(prev => ({ ...prev, loading: true }));
    try {
      const ids = Array.from(selectedIds);
      const result = await employeeMasterAPI.bulkDelete(ids, bulkDeleteDialog.hardDelete);
      toast.success(`${result.data.success} employees ${bulkDeleteDialog.hardDelete ? 'permanently deleted' : 'moved to trash'}`);
      if (result.data.failed > 0) {
        toast.warning(`${result.data.failed} employees could not be processed`);
      }
      setBulkDeleteDialog({ open: false, hardDelete: false, loading: false });
      clearSelection();
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bulk delete failed');
      setBulkDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Bulk restore handler
  const handleBulkRestore = async () => {
    setBulkRestoreDialog(prev => ({ ...prev, loading: true }));
    try {
      const ids = Array.from(selectedIds);
      const result = await employeeMasterAPI.bulkRestore(ids);
      toast.success(`${result.data.success} employees restored successfully`);
      if (result.data.failed > 0) {
        toast.warning(`${result.data.failed} employees could not be restored`);
      }
      setBulkRestoreDialog({ open: false, loading: false });
      clearSelection();
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bulk restore failed');
      setBulkRestoreDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Find duplicates handler
  const handleFindDuplicates = async () => {
    setDuplicatesDialog(prev => ({ ...prev, loading: true }));
    try {
      const result = await employeeMasterAPI.findDuplicates(duplicatesDialog.field);
      setDuplicatesDialog(prev => ({
        ...prev,
        loading: false,
        duplicates: result.data.duplicates || []
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to find duplicates');
      setDuplicatesDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Select employee for merge
  const selectForMerge = (emp, role) => {
    if (role === 'primary') {
      setMergeDialog(prev => ({
        ...prev,
        primaryId: emp.id,
        primary: emp
      }));
    } else {
      setMergeDialog(prev => ({
        ...prev,
        secondaryId: emp.id,
        secondary: emp
      }));
    }
  };

  // Merge employees handler
  const handleMerge = async () => {
    if (!mergeDialog.primaryId || !mergeDialog.secondaryId) {
      toast.error('Please select both primary and secondary employees');
      return;
    }
    setMergeDialog(prev => ({ ...prev, loading: true }));
    try {
      const result = await employeeMasterAPI.mergeEmployees(
        mergeDialog.primaryId,
        mergeDialog.secondaryId
      );
      toast.success(result.data.message);
      setMergeDialog({ open: false, loading: false, primaryId: null, secondaryId: null, primary: null, secondary: null });
      setDuplicatesDialog(prev => ({ ...prev, open: false }));
      fetchEmployees();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Merge failed');
      setMergeDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle column sorting with toggle
  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      // Toggle between asc and desc
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, start with ascending
      setSortBy(columnId);
      setSortOrder('asc');
    }
    setPage(1); // Reset to first page when sorting
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEmployees();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      department_id: '',
      status: '',
      authorization_status: '',
      employment_type: ''
    });
    setPage(1);
  };

  const clearSort = () => {
    setSortBy('');
    setSortOrder('asc');
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '-';
  };

  const handleRefreshStats = () => {
    fetchStats();
    fetchEmployees();
  };

  const handleExport = async () => {
    try {
      const params = {
        tab: activeTab !== 'total' ? activeTab : undefined,
        limit: 500 // Max allowed by backend
      };
      if (filters.search) params.search = filters.search;
      if (filters.department_id) params.department_id = filters.department_id;
      // Include sorting in export
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
      }
      
      // Use admin export endpoint for full data (no limit)
      const res = await api.get('/employee-master/export/all', { params });
      const data = res.data.data || [];
      
      // Convert to CSV with more fields for complete export
      const headers = [
        'Employee Code', 'Name', 'Login ID', 'Email', 'Mobile', 
        'Department', 'Designation', 'Grade', 'Status', 'Auth Status',
        'Date of Joining', 'Date of Birth', 'Employment Type', 'Gender'
      ];
      const csvContent = [
        headers.join(','),
        ...data.map(emp => [
          emp.employee_code || '',
          `"${emp.name || ''}"`,
          emp.login_id || '',
          emp.email || '',
          emp.mobile_primary || '',
          `"${emp.department_name || getDepartmentName(emp.department_id)}"`,
          `"${emp.designation || ''}"`,
          emp.grade || '',
          emp.status || '',
          emp.authorization_status || '',
          emp.date_of_joining || '',
          emp.date_of_birth || '',
          emp.employment_type || '',
          emp.gender || ''
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `employees_full_export_${activeTab}${sortBy ? `_sorted_${sortBy}_${sortOrder}` : ''}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Exported ${data.length} employees (complete dataset)${sortBy ? ` sorted by ${sortBy}` : ''}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Export access denied. Admin privileges required.');
      } else {
        toast.error('Export failed');
      }
    }
  };

  // Handle template download for bulk upload
  const handleDownloadTemplate = async () => {
    try {
      const response = await bulkUploadAPI.downloadTemplate();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'employee_upload_template.xlsx';
      link.click();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Handle bulk upload success - refresh data
  const handleBulkUploadSuccess = () => {
    fetchEmployees();
    fetchStats();
  };

  return (
    <div data-testid="employee-master-list">
      <Header 
        title="Employee Master - 360° HR Inventory"
        subtitle="Comprehensive employee management with maker-checker workflow"
        action={
          <div className="flex items-center gap-2">
            {/* Find Duplicates - Admin only */}
            {isAdmin() && (
              <Button 
                variant="outline" 
                onClick={() => setDuplicatesDialog({ open: true, loading: false, field: 'email', duplicates: [] })}
                data-testid="find-duplicates-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Find Duplicates
              </Button>
            )}
            {/* Bulk Select Toggle - Admin only */}
            {isAdmin() && (
              <Button 
                variant={selectionMode ? "secondary" : "outline"}
                onClick={() => {
                  setSelectionMode(!selectionMode);
                  if (selectionMode) clearSelection();
                }}
                data-testid="bulk-select-toggle"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectionMode ? 'Cancel Selection' : 'Bulk Select'}
              </Button>
            )}
            {/* Export button - Admin only */}
            {isAdmin() && (
              <Button variant="outline" onClick={handleExport} data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            )}
            {/* Bulk Upload button */}
            <Button variant="outline" onClick={() => setIsBulkUploadOpen(true)} data-testid="bulk-upload-btn">
              <FileUp className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => navigate('/employee-master/new')} data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
            <HelpTooltip moduleId="employee-master" />
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {/* Stats Tabs - Using Modular Component */}
        <EmployeeStatsCards
          stats={{
            total: stats.total || 0,
            active: stats.active || 0,
            pending_auth: stats.pending_auth || 0,
            probation: stats.on_probation || 0,
            incomplete_profile: stats.incomplete_profiles || 0,
            deleted: stats.deleted || 0
          }}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onRefresh={handleRefreshStats}
          loading={statsLoading}
        />

        {/* Active Tab Indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Showing:</span>
            <Badge variant="outline" className="font-medium">
              {TABS.find(t => t.id === activeTab)?.label} ({total})
            </Badge>
            {activeTab !== 'total' && (
              <Button variant="ghost" size="sm" onClick={() => handleTabChange('total')}>
                <XCircle className="w-4 h-4 mr-1" />
                Clear Filter
              </Button>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={handleRefreshStats} data-testid="refresh-stats">
            <RefreshCw className={`w-4 h-4 mr-1 ${statsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Bulk Action Bar - appears when items selected */}
        {selectionMode && selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between" data-testid="bulk-action-bar">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">{selectedIds.size} employee{selectedIds.size > 1 ? 's' : ''} selected</span>
              <Button variant="ghost" size="sm" onClick={clearSelection} className="text-blue-600 hover:text-blue-800">
                Clear selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {/* Bulk Delete - for non-deleted employees */}
              {activeTab !== 'deleted' && (
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => setBulkDeleteDialog({ open: true, hardDelete: false, loading: false })}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              )}
              {/* Bulk Restore - for deleted employees */}
              {activeTab === 'deleted' && (
                <Button 
                  variant="outline" 
                  className="border-green-300 text-green-600 hover:bg-green-50"
                  onClick={() => setBulkRestoreDialog({ open: true, loading: false })}
                  data-testid="bulk-restore-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restore Selected
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Search & Filters - Using Modular Component */}
        <EmployeeFilters
          filters={filters}
          setFilters={setFilters}
          departments={departments}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          activeTab={activeTab}
        />

        {/* Sort indicator */}
        {sortBy && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <span className="text-sm text-blue-700">
              Sorted by <strong>{SORTABLE_COLUMNS.find(c => c.id === sortBy)?.label || sortBy}</strong> ({sortOrder === 'asc' ? 'A → Z' : 'Z → A'})
            </span>
            <Button variant="ghost" size="sm" onClick={clearSort} className="h-6 px-2 text-blue-600 hover:text-blue-800">
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Employee Table - Using Modular Component */}
        <EmployeeTable
          employees={employees}
          loading={loading}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onDelete={(emp) => setDeleteDialog({ open: true, employee: emp, hardDelete: false, loading: false })}
          onRestore={handleRestoreEmployee}
          activeTab={activeTab}
          canSeeLoginId={canSeeLoginId}
          isAdmin={isAdmin()}
          getDepartmentName={getDepartmentName}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          tabLabel={TABS.find(t => t.id === activeTab)?.label}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteEmployeeDialog
          open={deleteDialog.open}
          onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}
          employee={deleteDialog.employee}
          hardDelete={deleteDialog.hardDelete}
          onHardDeleteChange={(checked) => setDeleteDialog(prev => ({ ...prev, hardDelete: checked }))}
          loading={deleteDialog.loading}
          onConfirm={handleDeleteEmployee}
        />

        {/* Pagination - Using Modular Component */}
        {totalPages > 1 && (
          <EmployeePagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        )}

        {/* Bulk Delete Dialog */}
        <BulkDeleteDialog
          open={bulkDeleteDialog.open}
          onOpenChange={(open) => setBulkDeleteDialog(prev => ({ ...prev, open }))}
          selectedCount={selectedIds.size}
          hardDelete={bulkDeleteDialog.hardDelete}
          onHardDeleteChange={(checked) => setBulkDeleteDialog(prev => ({ ...prev, hardDelete: checked }))}
          loading={bulkDeleteDialog.loading}
          onConfirm={handleBulkDelete}
        />

        {/* Bulk Restore Dialog */}
        <BulkRestoreDialog
          open={bulkRestoreDialog.open}
          onOpenChange={(open) => setBulkRestoreDialog(prev => ({ ...prev, open }))}
          selectedCount={selectedIds.size}
          loading={bulkRestoreDialog.loading}
          onConfirm={handleBulkRestore}
        />

        {/* Find Duplicates Dialog */}
        <Dialog open={duplicatesDialog.open} onOpenChange={(open) => setDuplicatesDialog(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-blue-600" />
                Find Duplicate Employees
              </DialogTitle>
              <DialogDescription>
                Find and manage potential duplicate employee records in the system.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Field selector */}
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-slate-700">Check duplicates by:</label>
                <Select 
                  value={duplicatesDialog.field} 
                  onValueChange={(v) => setDuplicatesDialog(prev => ({ ...prev, field: v, duplicates: [] }))}
                >
                  <SelectTrigger className="w-48" data-testid="duplicate-field-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="mobile_primary">Mobile Number</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="pan_number">PAN Number</SelectItem>
                    <SelectItem value="aadhaar_number">Aadhaar Number</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleFindDuplicates} disabled={duplicatesDialog.loading} data-testid="search-duplicates-btn">
                  {duplicatesDialog.loading ? 'Searching...' : 'Search'}
                </Button>
              </div>

              {/* Duplicates list */}
              {duplicatesDialog.duplicates.length === 0 && !duplicatesDialog.loading && (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No duplicates found. Click Search to scan for duplicates.</p>
                </div>
              )}

              {duplicatesDialog.duplicates.length > 0 && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Found <strong>{duplicatesDialog.duplicates.length}</strong> groups with potential duplicates
                  </p>
                  {duplicatesDialog.duplicates.map((group, idx) => (
                    <div key={group._id || `group-${idx}`} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="font-mono">
                          {duplicatesDialog.field}: {group._id}
                        </Badge>
                        <span className="text-sm text-slate-500">{group.count} records</span>
                      </div>
                      <div className="space-y-2">
                        {group.employees.map((emp) => (
                          <div key={emp.id || emp.employee_code} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                                {emp.name?.charAt(0) || '?'}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{emp.name}</p>
                                <p className="text-xs text-slate-500">{emp.employee_code} • {emp.department}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => {
                                  selectForMerge(emp, 'primary');
                                  setMergeDialog(prev => ({ ...prev, open: true }));
                                }}
                                data-testid={`merge-primary-${emp.id}`}
                              >
                                Keep as Primary
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                onClick={() => {
                                  selectForMerge(emp, 'secondary');
                                  setMergeDialog(prev => ({ ...prev, open: true }));
                                }}
                                data-testid={`merge-secondary-${emp.id}`}
                              >
                                Merge Into
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Merge Dialog */}
        <Dialog open={mergeDialog.open} onOpenChange={(open) => !mergeDialog.loading && setMergeDialog(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-purple-600">
                <Merge className="w-5 h-5" />
                Merge Employee Records
              </DialogTitle>
              <DialogDescription>
                Merge two employee records into one. The secondary employee will be marked as "merged" and their related records transferred.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Primary Employee */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-green-700">Primary Employee (Will be kept)</label>
                {mergeDialog.primary ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-medium">
                        {mergeDialog.primary.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium">{mergeDialog.primary.name}</p>
                        <p className="text-sm text-slate-500">{mergeDialog.primary.employee_code} • {mergeDialog.primary.email}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setMergeDialog(prev => ({ ...prev, primaryId: null, primary: null }))}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="p-3 border-2 border-dashed border-green-300 rounded-lg text-center text-green-600 text-sm">
                    Select an employee as Primary from the duplicates list
                  </div>
                )}
              </div>

              {/* Secondary Employee */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-amber-700">Secondary Employee (Will be merged & deactivated)</label>
                {mergeDialog.secondary ? (
                  <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-medium">
                        {mergeDialog.secondary.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium">{mergeDialog.secondary.name}</p>
                        <p className="text-sm text-slate-500">{mergeDialog.secondary.employee_code} • {mergeDialog.secondary.email}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setMergeDialog(prev => ({ ...prev, secondaryId: null, secondary: null }))}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="p-3 border-2 border-dashed border-amber-300 rounded-lg text-center text-amber-600 text-sm">
                    Select an employee as Secondary from the duplicates list
                  </div>
                )}
              </div>

              {/* Merge info */}
              {mergeDialog.primary && mergeDialog.secondary && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <strong>What will happen:</strong>
                  </p>
                  <ul className="text-sm text-purple-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Empty fields in {mergeDialog.primary.employee_code} will be filled from {mergeDialog.secondary.employee_code}</li>
                    <li>All leave applications, claims, and policies will be transferred</li>
                    <li>{mergeDialog.secondary.employee_code} will be marked as "merged" and deactivated</li>
                  </ul>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setMergeDialog({ open: false, loading: false, primaryId: null, secondaryId: null, primary: null, secondary: null })}
                disabled={mergeDialog.loading}
              >
                Cancel
              </Button>
              <Button 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleMerge}
                disabled={mergeDialog.loading || !mergeDialog.primaryId || !mergeDialog.secondaryId}
                data-testid="confirm-merge"
              >
                {mergeDialog.loading ? 'Merging...' : 'Merge Employees'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Modal */}
        <BulkUploadModal
          isOpen={isBulkUploadOpen}
          onClose={() => { setIsBulkUploadOpen(false); setBulkUploadFile(null); setBulkUploadResults(null); }}
          bulkUploadFile={bulkUploadFile}
          setBulkUploadFile={setBulkUploadFile}
          bulkUploadResults={bulkUploadResults}
          setBulkUploadResults={setBulkUploadResults}
          uploadingBulk={uploadingBulk}
          setUploadingBulk={setUploadingBulk}
          onDownloadTemplate={handleDownloadTemplate}
          onSuccess={handleBulkUploadSuccess}
        />
      </div>
    </div>
  );
};

export default EmployeeMasterList;
